import React, { useState, useEffect, useCallback } from 'react';
import styles from './ESimPackagesPage.module.css'; // Bu CSS dosyasını da güncelleyeceğiz
import apiClient from '../services/ApiClient';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { toast } from 'react-toastify';

// Vergi dahil fiyatları güvenli şekilde formatlar (string veya number gelebilir)
const formatMoney = (value) => {
    const n = parseFloat(value);
    return isNaN(n) ? '—' : n.toFixed(2);
};

const ESimPackagesPage = () => {
    const [allProducts, setAllProducts] = useState([]); // Ülkelere göre gruplanmış ürünler ve refill'leri tutar
    const [filteredProducts, setFilteredProducts] = useState([]); // Filtrelenmiş ve aranmış ürünler
    const [countriesForFilter, setCountriesForFilter] = useState([]);

    const [selectedCountryName, setSelectedCountryName] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const [editingStates, setEditingStates] = useState({});
    const [savingStates, setSavingStates] = useState({}); // { refillId: true/false }

    // --- API'den Veri Çekme ---
    const fetchPackagesFromApi = useCallback(async () => {
            setIsLoading(true);
            setError(null);
            try {
                // Endpoint'i kendi API'nize göre güncelleyin
                const response = await apiClient.get('/admin/countries-data');

                let rawData = [];
                if (response.data && Array.isArray(response.data.data)) {
                    rawData = response.data.data;
                } else {
                    throw new Error("Paket verileri alınamadı.");
                }

                // Gelen veri zaten `{ country, products: [{ ..., refills: [...] }] }` formatında olduğu için
                // ek bir transformasyona gerek yok. Direkt state'e atabiliriz.
                setAllProducts(rawData);

                const uniqueCountries = [];
                const countryNames = new Set();
                rawData.forEach(item => {
                    if (!countryNames.has(item.country)) {
                        countryNames.add(item.country);
                        uniqueCountries.push({ name: item.country, flag: item.flag });
                    }
                });
                setCountriesForFilter(uniqueCountries.sort((a, b) => a.name.localeCompare(b.name)));

                // Düzenleme state'ini ilk değerlerle doldur
                const initialEdits = {};
                rawData.forEach(countryItem => {
                    countryItem.products.forEach(product => {
                        product.refills.forEach(refill => {
                            const uniqueId = String(refill.refillId); // 'id' alanını benzersiz anahtar olarak kullanıyoruz
                            initialEdits[uniqueId] = {
                                basePriceTry: refill.basePriceTry,
                                isOpenGp: refill.isOpenGp,
                                isOpenAp: refill.isOpenAp,
                                isOpenWeb: refill.isOpenWeb
                            };
                        });
                    });
                });
                setEditingStates(initialEdits);

            } catch (err) {
                setError("Paketler yüklenirken bir hata oluştu.");
                console.error("Paket yükleme hatası:", err);
                setAllProducts([]);
            } finally {
                setIsLoading(false);
            }
    }, []);

    useEffect(() => {
        fetchPackagesFromApi();
    }, [fetchPackagesFromApi]);

    // --- Filtrelenmiş Paketleri Hesaplama ---
    useEffect(() => {
        if (!allProducts.length) {
            setFilteredProducts([]);
            return;
        }

        let filteredData = allProducts;

        // 1. Ülkeye göre filtrele
        if (selectedCountryName !== 'all') {
            filteredData = filteredData.filter(countryItem => countryItem.country === selectedCountryName);
        }

        // 2. Arama terimine göre filtrele
        if (searchTerm.trim() !== '') {
            const lowerSearchTerm = searchTerm.toLowerCase();
            filteredData = filteredData
                .map(countryItem => {
                    // Ürünleri veya refill'leri arama terimine göre filtrelenmiş yeni bir ülke objesi döndür
                    const matchingProducts = countryItem.products.filter(product => {
                        const productMatch = product.name.toLowerCase().includes(lowerSearchTerm) ||
                            product.description.toLowerCase().includes(lowerSearchTerm);

                        const refillMatch = product.refills.some(refill =>
                            refill.title.toLowerCase().includes(lowerSearchTerm)
                        );

                        return productMatch || refillMatch;
                    });

                    // Eğer eşleşen ürünler varsa, yeni ülke objesini bu ürünlerle oluştur
                    if (matchingProducts.length > 0) {
                        return { ...countryItem, products: matchingProducts };
                    }
                    return null; // Eşleşme yoksa bu ülkeyi dahil etme
                })
                .filter(Boolean); // null olanları (hiçbir ürünü eşleşmeyen ülkeleri) listeden çıkar
        }

        setFilteredProducts(filteredData);

    }, [allProducts, selectedCountryName, searchTerm]);


    const handleCountryFilterChange = (event) => setSelectedCountryName(event.target.value);
    const handleSearchChange = (event) => setSearchTerm(event.target.value);

    const handleInputChange = (refillId, field, value) => {
        setEditingStates(prev => ({
            ...prev,
            [refillId]: {
                ...prev[refillId],
                [field]: value
            }
        }));
    };

    const handleCheckboxChange = (refillId, field, checked) => {
        setEditingStates(prev => ({
            ...prev,
            [refillId]: {
                ...prev[refillId],
                [field]: checked
            }
        }));
    };

    const handleSaveChanges = async (productId, refillId, productName, refillTitle) => {
        const editedState = editingStates[refillId];
        if (!editedState) {
            console.error("Düzenleme durumu bulunamadı:", refillId);
            return;
        }

        const priceValueTL = parseFloat(editedState.basePriceTry);
        if (isNaN(priceValueTL) || priceValueTL < 0) {
            toast.error("Lütfen geçerli bir fiyat girin.");
            return;
        }
        setSavingStates(prev => ({ ...prev, [refillId]: true }));
        setError(null);

        const requestBody = {
            priceTL: priceValueTL,
            isOpenGp: editedState.isOpenGp,
            isOpenAp: editedState.isOpenAp,
            isOpenWeb: editedState.isOpenWeb,
            refillName: `${productName} - ${refillTitle}`, // Yeni parametre request body'ye eklendi
        };

        // Optimistic UI için orijinal veriyi bul
        let originalRefill = null;
        for (const country of allProducts) {
            for (const product of country.products) {
                const found = product.refills.find(r => String(r.refillId) === refillId);
                if (found) {
                    originalRefill = found;
                    break;
                }
            }
            if (originalRefill) break;
        }
        if (!originalRefill) {
            console.error("Orijinal refill bulunamadı, güncelleme geri alınıyor.");
            setSavingStates(prev => ({ ...prev, [refillId]: false }));
            return;
        }


        // --- DEĞİŞİKLİK BURADA: Optimistic UI Güncelleme Mantığı ---
        // State'i güncellemek için yeni bir obje oluşturuyoruz.
        const updatedRefillData = {
            ...originalRefill, // Diğer tüm alanlar orijinalden gelsin
            basePriceTry: String(priceValueTL.toFixed(2)), // Fiyatı formatlayarak string olarak ata

            isOpenGp: editedState.isOpenGp,
            isOpenAp: editedState.isOpenAp,
            isOpenWeb: editedState.isOpenWeb
        };

        setAllProducts(prevAllProducts =>
            prevAllProducts.map(country => ({
                ...country,
                products: country.products.map(product => ({
                    ...product,
                    refills: product.refills.map(refill =>
                        String(refill.refillId) === refillId ? updatedRefillData : refill
                    )
                }))
            }))
        );
        // --- /DEĞİŞİKLİK BURADA ---


        try {
            await apiClient.patch(`/esim-packages/refills/${refillId}`, requestBody);
            toast.success(`Paket (ID: ${refillId}) başarıyla güncellendi.`);

            // --- DEĞİŞİKLİK BURADA: Başarı durumunda editingStates'i de senkronize et ---
            // Bu, "değişiklik yapılmadı" gibi kontrollerin gelecekte doğru çalışmasını sağlar
            // ve UI'ın temel veri kaynağı ile tutarlı kalmasını garantiler.
            setEditingStates(prev => ({
                ...prev,
                [refillId]: {
                    basePriceTry: updatedRefillData.basePriceTry,

                    isOpenGp: updatedRefillData.isOpenGp,
                    isOpenAp: updatedRefillData.isOpenAp,
                    isOpenWeb: updatedRefillData.isOpenWeb
                }
            }));
            // --- /DEĞİŞİKLİK BURADA ---

        } catch (err) {
            setError(`Paket güncellenirken bir hata oluştu.`);
            console.error(`Refill ID ${refillId} güncellenirken API hatası:`, err);
            // Hata durumunda state'i orijinal veriyle geri al
            setAllProducts(prevAllProducts =>
                prevAllProducts.map(country => ({
                    ...country,
                    products: country.products.map(product => ({
                        ...product,
                        refills: product.refills.map(refill =>
                            String(refill.refillId) === refillId ? originalRefill : refill
                        )
                    }))
                }))
            );
        } finally {
            setSavingStates(prev => ({ ...prev, [refillId]: false }));
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.headerText}>
                    <h1 className={styles.title}>E-SIM Paketleri</h1>
                    <p className={styles.subtitle}>Ülke bazlı paketlerin fiyatlarını ve mağaza durumlarını yönetin.</p>
                </div>
                <div className={styles.infoNotes}>
                    <div className={styles.infoNote}>
                        <span className={styles.infoIcon}>ℹ️</span>
                        <span className={styles.infoText}>Girilen fiyatlar vergi hariçtir.</span>
                    </div>
                </div>
            </div>

            <div className={styles.filters}>
                <div className={styles.filterGroup}>
                    <label htmlFor="countryFilter">Ülkeye Göre Filtrele:</label>
                    <select id="countryFilter" value={selectedCountryName} onChange={handleCountryFilterChange} className={styles.selectInput}>
                        <option value="all">Tüm Ülkeler</option>
                        {countriesForFilter.map(country => (
                            <option key={country.name} value={country.name}>
                                {country.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div className={styles.filterGroup}>
                    <label htmlFor="searchFilter">Paket veya Ürün Ara:</label>
                    <input
                        type="text"
                        id="searchFilter"
                        placeholder="Andromeda, 1 GB, vs..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        className={styles.searchInput}
                    />
                </div>
            </div>

            <div className={styles.contentArea}>
                {isLoading ? (
                    <div className={styles.loadingContainer}><LoadingSpinner /></div>
                ) : error ? (
                    <div className={styles.errorPanel}>
                        <span className={styles.errorIcon}>⚠️</span>
                        <p className={styles.errorMessage}>Veriler alınamadı. Bağlantıyı kontrol edip tekrar deneyin.</p>
                        <button className={`${styles.retryButton} ghost`} onClick={() => fetchPackagesFromApi()}>Tekrar dene</button>
                    </div>
                ) : filteredProducts.length === 0 ? (
                    <p className={styles.emptyState}>Filtrelerinize uygun paket bulunamadı.</p>
                ) : (
                    filteredProducts.map(countryItem =>
                        countryItem.products.map(product => (
                            <div key={product.originalId} className={styles.productCard}>
                                <div className={styles.productHeader}>
                                    <img src={product.img} alt={product.name} className={styles.productImage} />
                                    <div className={styles.productInfo}>
                                        <h2 className={styles.productName}>{product.name} <span className={styles.countryTag}>{countryItem.country}</span></h2>
                                        <p className={styles.productDescription}>{product.description}</p>
                                    </div>
                                </div>
                                <div className={styles.tableWrap}>
                                <table className={styles.refillsTable}>
                                    <thead>
                                        <tr>
                                            <th>Paket</th>
                                            <th className={styles.numCol}>Fiyat (TL)</th>
                                            <th className={styles.toggleCol}>Google Play</th>
                                            <th className={styles.toggleCol}>App Store</th>
                                            <th className={styles.toggleCol}>Web</th>
                                            <th className={styles.actionCol}>İşlemler</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {product.refills.map(refill => {
                                            const uniqueId = String(refill.refillId);
                                            const currentEditState = editingStates[uniqueId] || {};
                                            const isSavingThisRow = savingStates[uniqueId] || false;

                                            return (
                                                <tr key={uniqueId}>
                                                    <td className="num">{refill.title}</td>
                                                    <td className={styles.numCol}>
                                                        <input type="number" value={currentEditState.basePriceTry || ''} onChange={(e) => handleInputChange(uniqueId, 'basePriceTry', e.target.value)} className={styles.priceInput} step="0.01" disabled={isSavingThisRow} />
                                                        <div className={styles.taxPrices} title="Vergi dahil satış fiyatları">
                                                            <span className={styles.taxLabel}>Vergi dahil</span>
                                                            <span className={`${styles.taxVals} num`}>₺{formatMoney(refill.priceTL)} · €{formatMoney(refill.priceEuro)} · ${formatMoney(refill.priceUsd)}</span>
                                                        </div>
                                                    </td>
                                                    <td className={styles.toggleCell}>
                                                        <label className={styles.switch}>
                                                            <input type="checkbox" checked={currentEditState.isOpenGp || false} onChange={(e) => handleCheckboxChange(uniqueId, 'isOpenGp', e.target.checked)} disabled={isSavingThisRow} />
                                                            <span className={`${styles.slider} ${styles.round}`}></span>
                                                        </label>
                                                    </td>
                                                    <td className={styles.toggleCell}>
                                                        <label className={styles.switch}>
                                                            <input type="checkbox" checked={currentEditState.isOpenAp || false} onChange={(e) => handleCheckboxChange(uniqueId, 'isOpenAp', e.target.checked)} disabled={isSavingThisRow} />
                                                            <span className={`${styles.slider} ${styles.round}`}></span>
                                                        </label>
                                                    </td>
                                                    <td className={styles.toggleCell}>
                                                        <label className={styles.switch}>
                                                            <input type="checkbox" checked={currentEditState.isOpenWeb || false} onChange={(e) => handleCheckboxChange(uniqueId, 'isOpenWeb', e.target.checked)} disabled={isSavingThisRow} />
                                                            <span className={`${styles.slider} ${styles.round}`}></span>
                                                        </label>
                                                    </td>
                                                    <td className={styles.actionCol}>
                                                        <button onClick={() => handleSaveChanges(product.originalId, uniqueId, product.name, refill.title)} className={styles.saveButton} disabled={isSavingThisRow}>
                                                            {isSavingThisRow ? 'Kaydediliyor...' : 'Kaydet'}
                                                        </button>
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                                </div>
                            </div>
                        ))
                    )
                )}
            </div>
        </div>
    );
};

export default ESimPackagesPage;